<template> 
  <div v-loading="isLoading">
    <vue-scheduler disable-dialog :time-range='calendarTimeRange' event-display="comments" :initial-date="showDate" :show-time-marker="isCurrentWeek" class="calendar" id="calendar" :users="users" :events="events" :onZoom="zoom" :zoomFit="zoomFit"></vue-scheduler>
  </div>
</template>

<script>
  import VueScheduler from './v-calendar'
  import { getRangeIndexFromDateRange, getDateRangeFromRangeIndex } from '@/utils/timeRange'
  import { arraysEqual } from '@/utils/utils'
  import Vue from 'vue'
  import moment from 'moment'
  
  Vue.use(VueScheduler)

  export default {
    props: {
      users: Array
    },
    data: function () {
      return {
        isLoading: false
      }
    },
    computed: {
      timeEntries () {
        this.$store.commit('updateFilteredEntries', this.$store.getters.timeEntries)
        return this.$store.getters.timeEntries
      },
      calendarTimeRange () {
        let entries = this.events
        let currentDateRange = this.$store.getters.currentDateRange
        // Filter by search terms and date range
        entries = entries.filter(entry => {
          return entry.startTime >= moment(currentDateRange[0]) && entry.startTime <= moment(currentDateRange[1])
        })
        let times = []
  
        entries.forEach(entry => {
          times.push(entry.startTime.hours())
          times.push(entry.endTime.hours())
        })
  
        let minTime = Math.min(...times)
        let maxTime = Math.max(...times)

        let minOut = 8
        let maxOut = 17
  
        if (minTime >= 0 && minTime <= 8) {
          minOut = minTime
        }
        if (maxTime >= 17 && maxTime <= 24) {
          maxOut = maxTime
        }
  
        return [minOut, maxOut]
      },
      showDate () {
        let rangeIndex = getRangeIndexFromDateRange(this.$store.getters.currentDateRange, moment())
        var dateString = getDateRangeFromRangeIndex(rangeIndex.range, rangeIndex.index)[0]
        var date = new Date(dateString)
        return date
      },
      isCurrentWeek: function () {
        return arraysEqual(this.$store.getters.currentDateRange, getDateRangeFromRangeIndex('week', 0))
      },
      events: function () {
        let events = []
        let results = []
        let entries = this.timeEntries || []

        if (this.$store.getters.adminMode) {
          entries = entries.filter(entry => !entry.isPersonal)
        }
  
        entries.forEach((event) => {
          let found = results.indexOf(event.client)
          if (found === -1) {
            results.push(event.client)
          }
          if (event.start.date() !== moment(event.end).date() && event.start.isSame(event.end, 'week')) {
            let newEventBeforeMidnight = {
              id: event.id,
              date: event.date,
              startTime: event.start,
              endTime: moment(event.start).endOf('day'),
              totalTime: moment.duration(moment(event.start).endOf('day').diff(event.start)).asHours().toFixed(1) + 'h',
              client: event.client,
              project: event.projectPath,
              task: event.task && event.task.attributes.name,
              description: event.description,
              duration: moment.duration(moment(event.start).endOf('day').diff(event.start)).asHours().toFixed(1),
              isPersonal: event.isPersonal,
              isSplitStart: true,
              isPTO: event.isPTO,
              isHoliday: event.isHoliday
            }
            let newEventAfterMidnight = {
              id: event.id,
              date: event.end,
              startTime: moment(event.end).startOf('day'),
              endTime: event.end,
              totalTime: moment.duration(event.end.diff(moment(event.end).startOf('day'))).asHours().toFixed(1) + 'h',
              client: event.client,
              project: event.projectPath,
              task: event.task && event.task.attributes.name,
              description: event.description,
              duration: moment.duration(event.end.diff(moment(event.end).startOf('day'))).asHours().toFixed(1),
              isPersonal: event.isPersonal,
              isSplitEnd: true,
              isPTO: event.isPTO,
              isHoliday: event.isHoliday
            }
            events.push(newEventBeforeMidnight)
            events.push(newEventAfterMidnight)
          } else { // Otherwise treat it as one event.
            let newEvent = {
              id: event.id,
              date: event.date,
              startTime: event.start,
              endTime: event.end || moment(),
              totalTime: event.duration + 'h',
              client: event.client,
              project: event.projectPath,
              task: event.task && event.task.attributes.name,
              description: event.description,
              duration: event.duration,
              isPersonal: event.isPersonal,
              isPTO: event.isPTO,
              isHoliday: event.isHoliday
            }
            events.push(newEvent)
          }
        })
        events.forEach(event => {
          let index = results.indexOf(event.client)
          event.class = 'event-' + index
          if (event.isPersonal) {
            event.class = 'personal'
          }
          if (event.isPTO) {
            event.class = 'pto'
          }
          if (event.isHoliday) {
            event.class = 'holiday'
          }
        })
        return events
      },
      timeEntryFilters () {
        return this.$store.getters.timeEntryFilters
      }
    },

    watch: {
      timeEntryFilters: function () {
        this.isLoading = true
        this.$store.dispatch('time-entries/loadAll', this.$store.getters.timeEntryFilters).then(() => { this.isLoading = false })
      }
    },

    methods: {
      zoom (e) {
        let deltaRaw = e.wheelDelta ? e.wheelDelta : e
        // let delta = deltaRaw / 120
        let sign = Math.sign(deltaRaw)
        let hourBlocks = this.$el.querySelectorAll('.v-cal-days .v-cal-day__hour-block, .v-cal-days .v-cal-hour')
        let currentPadding = parseFloat(hourBlocks[0].style.paddingTop || window.getComputedStyle(hourBlocks[0], null).getPropertyValue('padding-top'))
        let newPadding = `${currentPadding + (currentPadding * 0.3 * sign)}px`

        hourBlocks.forEach(hourBlock => {
          hourBlock.style.paddingTop = newPadding
          hourBlock.style.paddingBottom = newPadding
        })
        if (e.preventDefault) {
          e.preventDefault()
        }
      },
      zoomFit () {
        var rawHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
        let height = rawHeight - 70 - 40 - 87
        let hourBlocks = this.$el.querySelectorAll('.v-cal-days .v-cal-day__hour-block, .v-cal-days .v-cal-hour')
        let newPadding = ((height / (hourBlocks.length / 8)) - 15) / 2
        hourBlocks.forEach(hourBlock => {
          hourBlock.style.paddingTop = `${newPadding}px`
          hourBlock.style.paddingBottom = `${newPadding}px`
        })
      }
    },

    created () {
      this.$nextTick(() => {
        this.zoomFit()
        // this.$el.addEventListener('mousewheel', this.zoom, false)
      })
    }
  }
</script>

<style  scoped lang='scss'> 
  @import '../styles/common.scss';
</style>

<style lang='scss'>
@import '../styles/colors.scss';

$v-cal-event-bg:transparent;
$v-cal-content-border-color: transparent;
$v-cal-days-head-bg: transparent;
$v-cal-body-shadow: none;
$v-cal-time-marker-color: $light-red;

@import "./v-calendar/scss/main.scss";

.calendar{
  padding: 20px;
  
  .all-day, .v-cal-event-time{
    display:none;
  }
  .personal{
    background-image: $personal-color-calendar !important;
  }
  .pto{
    background: $pto-color-calendar !important;
    .v-cal-event-name{
      color: #444444 !important;
    }
    
  }
  .holiday{
    background: $holiday-color-calendar !important;
    .v-cal-event-name{
      color: #444444 !important;
    }
  }
  @for $i from 1 through length($event-colors) {
    .event-#{$i - 1} {
      background: nth($event-colors, $i);
    }
    .event-#{$i - 1} .split-indicator {
      color: nth($event-colors, $i) !important;
    }
  }
  .v-cal-days .v-cal-hour {
    transition: all 0.3s ease-in-out;
  }
}
</style>
